import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getManifest,
  type ChapterImage,
  type Citation,
  type Manifest,
  type Part,
  type Sentence,
  type Spans,
} from "../api";
import {
  afterPart,
  allSentences,
  clock,
  fractionOf,
  isComplete,
  offsetWithinPart,
  partAt,
  sentenceAt,
  sentenceForCitation,
  wordAt,
  playingPart,
  positionInPart,
} from "./timeline";
import ChapterFigure from "../components/ChapterFigure";
import { PicturePlace } from "../components/PictureViewer";
import { useT } from "../i18n";
import SpeedButton from "./SpeedButton";
import { useSpeed } from "./speed";
import AskedMark, { askedLines, askedWords } from "./AskedMark";
import ChatSheet from "./ChatSheet";
import { useThreads } from "./conversation";
import ChangeSheet from "./ChangeSheet";
import CorrectionSheet from "./CorrectionSheet";
import { planBySentence, TOP as FIGURE_TOP } from "./figures";
import { ATTEMPTS_BEFORE_TELLING, linksStale, MAX_ATTEMPTS, retryDelay, sourceAt } from "./resume";
import { useAutoScroll } from "./useAutoScroll";
import { cleanWord } from "./pressedWord";
import { usePressGesture } from "./usePressGesture";
import { SharedAudio } from "./sharedAudio";
import AskButton from "./AskButton";
import { withGaps } from "./paragraphGap";
import { SpeakerMark, utf8Length, withSpeakers, type Speakers } from "./speakers";
import { sectionNumberOf } from "./blocks";
import SectionRule from "./SectionRule";

/**
 * STALL_CHECK_MS is how often the player checks that the clock is still moving.
 *
 * Not every interruption announces itself: an element whose data runs out sits
 * at the same `currentTime` for ever without firing `error`. Five seconds is
 * long enough to be free and short enough that a listener with their phone in
 * a pocket does not notice the gap.
 */
const STALL_CHECK_MS = 5_000;

/**
 * PAUSE_GRACE_MS is how long after this player moves the element to another
 * source a `pause` from it still counts as that move rather than an
 * interruption.
 */
const PAUSE_GRACE_MS = 1_000;

/** The media-session buttons this player answers. */
const SESSION_ACTIONS: MediaSessionAction[] = [
  "play",
  "pause",
  "seekbackward",
  "seekforward",
  "seekto",
];

/**
 * Player plays a chapter and highlights the text as it goes.
 *
 * One <audio> element, moved between parts, rather than one per part: browsers
 * limit concurrent media elements, and a chapter can have 130 parts.
 */
export default function Player({
  chapterID,
  chapterNumber,
  bookID,
  draft = false,
  manifest: initialManifest,
  startAtMs,
  onPosition,
  allowCorrections = false,
  allowAsking = false,
  onEditLine,
  allowRewrite = false,
  images = [],
  footer,
  title,
  speakers,
  onEnd,
  onListenerMove,
}: {
  chapterID: string;
  /** The chapter as the reader's list numbers it, for the chat (ADR-0176). */
  chapterNumber?: number;
  /** The book a reader's conversation belongs to. */
  bookID?: string;
  /** Whose audio: the working copy's (studio) or the published edition's. */
  draft?: boolean;
  manifest: Manifest;
  startAtMs: number;
  onPosition: (positionMs: number, completed: boolean) => void;
  /**
   * Whether a long press opens the change sheet: pronunciation, editing and
   * rewriting (ADR-0025).
   *
   * Off for a reader. Correcting a pronunciation edits the book for everyone who
   * listens to it and spends the owner's budget to preview — it belongs to
   * whoever is making the book, not to whoever is reading it. The player is the
   * same component either way; only this changes.
   */
  allowCorrections?: boolean;
  /**
   * Whether a long press opens the chat, and the transport offers it.
   *
   * The reader's half of the same gesture. Correcting edits the book for
   * everyone; asking changes nothing and is paid for by the reader (ADR-0013).
   * The two are mutually exclusive because one gesture cannot do both — the
   * studio corrects, the reader asks.
   */
  allowAsking?: boolean;
  /**
   * Opens the editor on a line from the studio's change sheet (ADR-0025), with
   * the rewrite sheet when `rewrite` is set. Absent where the text cannot be
   * edited, and then the sheet does not offer it.
   */
  onEditLine?: (line: string, rewrite: boolean) => void;
  /** Whether the change sheet offers an AI rewrite. */
  allowRewrite?: boolean;
  /**
   * The chapter's pictures (ADR-0038), laid out among the spoken sentences.
   *
   * They are placed by the byte offset the server resolved rather than by a
   * paragraph count: this transcript is the text as it was *narrated*, and a
   * chapter edited since has a different number of paragraphs.
   */
  images?: ChapterImage[];
  /** What follows the text, kept clear of the pinned transport. */
  footer?: ReactNode;
  /**
   * Who says each quote, placed by the server in this manifest's revision
   * (ADR-0125). The studio's; a reader passes nothing.
   */
  speakers?: Speakers | null;
  /**
   * What the lock screen calls this chapter.
   *
   * Optional because a player with no title still plays; the media session
   * simply has nothing to put under the buttons.
   */
  title?: string;
  /** The chapter's audio reached its real end (ADR-0063): auto-play's cue. */
  onEnd?: () => void;
  /** The listener pressed play or moved: either cancels an auto-play countdown. */
  onListenerMove?: (move: "play" | "seek") => void;
}) {
  // The reader page's element when there is one (sharedAudio.ts), else our own.
  const shared = useContext(SharedAudio);
  const audio = useRef<HTMLAudioElement | null>(null);
  if (shared) audio.current = shared;
  const t = useT();
  const [speed, setSpeed] = useSpeed();
  const [manifest, setManifest] = useState(initialManifest);
  const [positionMs, setPositionMs] = useState(startAtMs);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * The three facts recovery needs, in refs.
   *
   * Everything below this point is reached from a listener registered once —
   * the element's own events, `online`, `visibilitychange`, a timer — and each
   * of those outlives the render that created it. Read through state they would
   * recover to wherever the listener was installed, which on a phone that has
   * been asleep for a minute is a position a minute old and a manifest whose
   * links have expired.
   */
  const wanted = useRef(false);
  const positionRef = useRef(startAtMs);
  const manifestRef = useRef(initialManifest);
  /**
   * Until when a `pause` from the element is this player's own doing.
   *
   * Moving to another source pauses it — the load algorithm fires `pause`
   * before it fetches anything — so without this every part boundary would look
   * like an interruption and be "recovered" from. A window rather than a flag,
   * because one source change can fire more than one `pause` and a flag that is
   * consumed by the first lets the second through.
   */
  const expectPauseUntil = useRef(0);
  /** How many attempts have been made to pick playback back up since it last ran. */
  const attempts = useRef(0);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recovering = useRef(false);
  // The word being corrected, with the sentence it sits in: the sheet needs
  // both, because what gets previewed is the sentence and what gets replaced is
  // the word.
  const [correcting, setCorrecting] = useState<{
    word: string;
    sentence: string;
  } | null>(null);
  // The line a studio long press landed on, while its change sheet is open.
  const [changing, setChanging] = useState<{
    word: string;
    sentence: string;
  } | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  // The line the next question is about: the one long-pressed, or the one
  // being read when the chat button was tapped.
  const [chatContext, setChatContext] = useState<string | null>(null);
  const threads = useThreads(bookID ?? chapterID);
  // Whether the reader chose that line, by pressing it or its mark, rather
  // than the chat button choosing it for them: only a chosen line offers
  // "who is …?" (ADR-0045).
  const [chatChosen, setChatChosen] = useState(false);
  // The word pressed on the line, for Meaning (ADR-0141).
  const [chatWord, setChatWord] = useState<string | undefined>();
  const openChat = (sentence: string | null, chosen = true, word?: string) => {
    setChatContext(sentence);
    setChatChosen(chosen);
    setChatWord(word);
    setChatOpen(true);
  };

  /**
   * seeking guards a just-issued seek from being undone.
   *
   * Setting currentTime takes effect asynchronously, and a timeupdate queued
   * before it lands still carries the old position — so on a slow device the
   * highlight snaps back to where the listener tapped away from. The flag is
   * cleared by the element's own `seeked` event, which is the only thing that
   * actually knows the move is done.
   */
  const seeking = useRef(false);

  const sentences = useMemo(() => allSentences(manifest), [manifest]);
  const figures = useMemo(() => planBySentence(sentences, images), [sentences, images]);
  const asked = askedLines(threads.lines, chatContext);
  const activeSentence = sentenceAt(sentences, positionMs);
  const scroll = useAutoScroll(
    activeSentence,
    activeSentence >= 0 ? wordAt(sentences[activeSentence]!, positionMs) : null,
  );

  const currentPart = partAt(manifest, positionMs);
  // Refreshing the manifest re-mints every signed URL. It is the recovery path
  // for a 403 mid-playback, and it is deliberately cheap: nothing about the
  // audio changed, only the links to it.
  const refreshing = useRef(false);
  const refreshManifest = useCallback(async (): Promise<Manifest | null> => {
    if (refreshing.current) return null;
    refreshing.current = true;
    try {
      const fresh = await getManifest(chapterID, { draft });
      manifestRef.current = fresh;
      setManifest(fresh);
      return fresh;
    } catch {
      return null;
    } finally {
      refreshing.current = false;
    }
  }, [chapterID, draft]);

  useEffect(() => {
    manifestRef.current = manifest;
  }, [manifest]);
  useEffect(() => {
    positionRef.current = positionMs;
  }, [positionMs]);

  /**
   * pointAt moves the element onto a part, and is the only way to do it.
   *
   * Three lines that must never drift apart: the part's index is what
   * `playingPart` reads back, and the pause grace has to be open *before* the
   * source changes, because the load algorithm fires `pause` before it fetches
   * anything and a `pause` outside the grace is an interruption to recover
   * from. Four callers set a source — the first part, a seek, a boundary and a
   * recovery — and a fifth that forgot the grace would stop every chapter at
   * its next part.
   */
  const pointAt = useCallback((el: HTMLAudioElement, part: Part, src?: string) => {
    el.dataset.partIndex = String(part.index);
    expectPauseUntil.current = Date.now() + PAUSE_GRACE_MS;
    el.src = src ?? part.url;
  }, []);

  const cancelRetry = useCallback(() => {
    if (retryTimer.current) clearTimeout(retryTimer.current);
    retryTimer.current = null;
  }, []);

  /**
   * resume points the element back at where playback stopped and asks it to
   * play, then schedules the next attempt in case this one does not take.
   *
   * The old recovery was one manifest refresh and, if that failed, a message
   * telling the listener to reload the page — which is the wrong shape for the
   * commonest interruption there is. A phone going to sleep drops the
   * connection the audio is streaming over *and* the connection a refresh would
   * need, so the one thing the page could still have done, it gave up on.
   *
   * Nothing here knows whether an attempt worked: `play()` resolves before the
   * first sample is heard. The element's own `playing` event is what cancels the
   * next attempt, which is also the only thing that knows sound is coming out.
   *
   * Kept behind a ref rather than in the dependency list of the five listeners
   * that call it: it refers to itself, and a fresh identity every render would
   * re-register all of them four times a second.
   */
  const resumeImpl = useRef<() => Promise<void>>(async () => {});
  const resume = useCallback(() => {
    void resumeImpl.current();
  }, []);
  resumeImpl.current = async () => {
    if (!wanted.current || recovering.current) return;
    recovering.current = true;
    cancelRetry();
    const attempt = attempts.current;
    attempts.current = attempt + 1;
    try {
      const el = audio.current;
      if (!el) return;
      const target = positionRef.current;
      // The links are usually not the problem — the radio was asleep, not the
      // signature — so the first attempt costs no round trip of its own. A
      // later one re-mints them, and so does any attempt made once they are
      // near enough to expiry to lose the retry.
      let fresh = manifestRef.current;
      if (attempt > 0 || linksStale(fresh.expires_at, Date.now())) {
        fresh = (await refreshManifest()) ?? fresh;
      }
      const part = partAt(fresh, target);
      if (!part) return;
      pointAt(el, part, sourceAt(part.url, offsetWithinPart(part, target)));
      // Asked to play before anything has loaded, deliberately: a hidden page
      // loads no media at all until something asks it to, which is why the
      // offset travels in the source rather than being set afterwards.
      el.play().catch(() => {});
      if (attempts.current >= ATTEMPTS_BEFORE_TELLING) setError(t.player.playbackRetrying);
    } finally {
      recovering.current = false;
      if (wanted.current && attempts.current < MAX_ATTEMPTS) {
        retryTimer.current = setTimeout(resume, retryDelay(attempt));
      } else if (wanted.current) {
        // Out of patience. The listener is told, the transport goes back to
        // Play, and pressing it — here or on the lock screen — starts the
        // count again from where they stopped.
        wanted.current = false;
        setPlaying(false);
        setError(t.player.playbackFailed);
      }
    }
  };

  /** start and stop are the one way in and out of playing, for every control. */
  const start = useCallback(() => {
    const el = audio.current;
    if (!el) return;
    onListenerMove?.("play");
    wanted.current = true;
    attempts.current = 0;
    setError(null);
    setPlaying(true);
    el.play().catch(() => {
      // Refused — a source that has gone away, or no gesture behind this call.
      // Recovery is the same path as an interruption rather than a dead end.
      resume();
    });
  }, [resume, onListenerMove]);

  const stop = useCallback(() => {
    const el = audio.current;
    if (!el) return;
    wanted.current = false;
    cancelRetry();
    // The one pause this player asks for itself; everything else that pauses
    // the element is an interruption.
    expectPauseUntil.current = Date.now() + PAUSE_GRACE_MS;
    el.pause();
    setPlaying(false);
  }, [cancelRetry]);

  /**
   * seek moves to a chapter position, switching parts if necessary.
   *
   * The subtlety is that setting currentTime right after load() does nothing:
   * load() resets the element and clears the position asynchronously, so the
   * assignment is overwritten a moment later and the player silently stays
   * where it was. When the source changes, the position is applied once the
   * element has metadata.
   */
  const seek = useCallback(
    (target: number) => {
      const el = audio.current;
      const part = partAt(manifest, target);
      if (!el || !part) return;
      onListenerMove?.("seek");
      setPositionMs(target);
      positionRef.current = target;

      const within = offsetWithinPart(part, target) / 1000;
      const sameSource = el.dataset.partIndex === String(part.index) && !!el.getAttribute("src");
      if (sameSource) {
        seeking.current = true;
        el.currentTime = within;
        return;
      }

      pointAt(el, part);
      el.load();
      const apply = () => {
        seeking.current = true;
        el.currentTime = within;
        if (playing) void el.play();
      };
      if (el.readyState >= 1) apply();
      else el.addEventListener("loadedmetadata", apply, { once: true });
    },
    [manifest, playing, pointAt, onListenerMove],
  );

  const navigate = useNavigate();
  const location = useLocation();
  /**
   * visitCitation takes the reader to the passage an answer came from.
   *
   * Two cases, because retrieval reaches earlier chapters. A citation from this
   * chapter is a seek; one from another chapter is a navigation, carrying the
   * cited text in router state so the destination can do the same seek on
   * arrival. The text travels rather than an offset for the reason in
   * sentenceForCitation: the two coordinate systems do not agree.
   */
  const visitCitation = useCallback(
    (citation: Citation) => {
      if (citation.chapter_id !== chapterID) {
        navigate(`/read/${citation.chapter_id}`, {
          state: { citation: citation.text },
        });
        return;
      }
      const at = sentenceForCitation(sentences, citation.text);
      // Not found means this chapter has been re-narrated since the passage was
      // indexed. Closing the sheet on a silent no-op would read as a broken
      // link, so the sheet stays open and the reader keeps the quotation.
      const target = sentences[at];
      if (!target) return;
      setChatOpen(false);
      seek(target.start_ms);
    },
    [chapterID, navigate, seek, sentences],
  );

  /**
   * Arriving from a citation in another chapter: seek to the cited passage
   * once, then clear the state so a refresh or a back-and-forward does not
   * drag the reader back to it.
   */
  useEffect(() => {
    const cited = (location.state as { citation?: string } | null)?.citation;
    if (!cited) return;
    const at = sentenceForCitation(sentences, cited);
    navigate(location.pathname, { replace: true, state: null });
    const target = sentences[at];
    if (!target) return;
    seek(target.start_ms);
  }, [location.pathname, location.state, navigate, seek, sentences]);

  /**
   * The chosen speed, applied to the element and kept applied.
   *
   * `defaultPlaybackRate` as well as `playbackRate`, and this is the whole
   * subtlety: every part is played through the one <audio> element, and
   * `load()` — which moving to the next part calls — resets `playbackRate` to
   * `defaultPlaybackRate`. Setting only the first would play part one at 1.5×
   * and the rest of the chapter at normal speed.
   */
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    el.defaultPlaybackRate = speed;
    el.playbackRate = speed;
  }, [speed, manifest]);

  // Load the starting part once, at the saved position.
  useEffect(() => {
    const el = audio.current;
    const part = partAt(manifest, startAtMs);
    if (!el || !part || el.dataset.partIndex) return;
    pointAt(el, part);
    const at = offsetWithinPart(part, startAtMs) / 1000;
    if (at > 0) {
      // Same rule as seek: the element has just been given a source and cannot
      // take a position until it has metadata.
      const apply = () => {
        el.currentTime = at;
      };
      if (el.readyState >= 1) apply();
      else el.addEventListener("loadedmetadata", apply, { once: true });
    }
  }, [manifest, startAtMs, pointAt]);

  /**
   * Arriving by auto-play (ADR-0177): play at once, on the element the
   * listener already started. A browser that still refuses opens the chapter
   * paused with Play showing — never an error, and never the recovery loop,
   * which would retry a refusal no retry can change.
   */
  const autoStarted = useRef(false);
  useEffect(() => {
    const auto = (location.state as { autoplay?: boolean } | null)?.autoplay;
    const el = audio.current;
    if (!auto || !el || autoStarted.current) return;
    autoStarted.current = true;
    navigate(location.pathname, { replace: true, state: null });
    wanted.current = true;
    setPlaying(true);
    el.play().catch(() => {
      wanted.current = false;
      setPlaying(false);
    });
  }, [location.pathname, location.state, navigate]);

  /**
   * reportPosition keeps the lock screen's scrubber honest.
   *
   * Throttled to once a second against timeupdate's four: this is the whole
   * chapter's timeline, not the part's, so it is the only place the phone
   * learns that a 20-minute chapter is not a 40-second file.
   */
  const lastReport = useRef(0);
  const reportPosition = (at: number, rate: number) => {
    const session = navigator.mediaSession;
    if (!session?.setPositionState) return;
    const now = Date.now();
    if (now - lastReport.current < 1000) return;
    lastReport.current = now;
    const duration = manifest.duration_ms / 1000;
    try {
      session.setPositionState({
        duration,
        position: Math.min(Math.max(at / 1000, 0), duration),
        playbackRate: rate > 0 ? rate : 1,
      });
    } catch {
      /* a duration this session will not take; the scrubber is not worth an error */
    }
  };

  const handleTimeUpdate = () => {
    const el = audio.current;
    if (!el) return;
    // A timeupdate that was already queued when the seek was issued reports the
    // position we just moved away from. Dropping it is the difference between
    // a tap that sticks and one that visibly bounces back.
    if (seeking.current) return;
    // Measured against the part whose audio is loaded, and kept inside it: see
    // playingPart for the part this used to skip.
    const part = playingPart(manifest, el.dataset.partIndex) ?? currentPart;
    if (!part) return;
    const at = positionInPart(part, el.currentTime * 1000);
    setPositionMs(at);
    positionRef.current = at;
    onPosition(at, isComplete(manifest, at));
    reportPosition(at, el.playbackRate);
  };

  const handleEnded = () => {
    // The part that just finished is the one loaded, whatever the clock says.
    const finished = playingPart(manifest, audio.current?.dataset.partIndex) ?? currentPart;
    // By position in the list, and "the end" only at the last part (ADR-0063).
    // This used to ask for the part whose index was one higher, which is a hole
    // on any partial render — it stopped the owner's chapter at its first
    // failed part and wrote the chapter down as finished there.
    const { next, end } = afterPart(manifest, finished);
    if (!next) {
      // The chapter is over, so nothing is left to recover: the watchdog must
      // not read the element sitting at its end as an interruption.
      wanted.current = false;
      cancelRetry();
      setPlaying(false);
      // Reaching the end is unambiguous completion, whatever the threshold
      // says — but only the real end. Completion is sticky on the server.
      if (end) {
        onPosition(manifest.duration_ms, true);
        onEnd?.();
      }
      return;
    }
    // Not seek(): that waits for metadata before playing, and a finished part
    // leaves the element paused. Chrome on Android loads nothing for a hidden
    // page until something asks it to play, so with the screen off the wait
    // never ended and the chapter stopped at the first part boundary. The next
    // part starts at 0, so there is no position to wait for — ask to play now.
    const el = audio.current;
    if (!el) return;
    setPositionMs(next.start_ms);
    positionRef.current = next.start_ms;
    pointAt(el, next);
    // Rejected only when the source changes again before playback starts — a
    // tap during the handoff — which is not a failure.
    if (playing) el.play().catch(() => {});
  };

  /**
   * The transport button, from what the listener asked for rather than from
   * `el.paused`.
   *
   * They are not the same thing any more: while recovery is in flight the
   * element is paused and the listener still wants the chapter, and a button
   * that read the element would offer Play and then start a second attempt
   * instead of stopping the one already running.
   */
  const toggle = () => {
    if (playing) stop();
    else start();
  };

  /**
   * The element stopped without this player asking it to.
   *
   * Moving between parts pauses it too, which is what expectPause covers.
   * Anything else — the phone going to sleep and taking the connection with it,
   * another app taking the audio — is an interruption, and the listener asked
   * for this chapter to keep playing.
   */
  const handlePause = () => {
    if (Date.now() < expectPauseUntil.current) return;
    // The end of a part is not an interruption: handleEnded decides what plays
    // next, and it has not run yet.
    if (!wanted.current || audio.current?.ended) return;
    resume();
  };

  /** Sound is coming out: whatever went wrong is over. */
  const handlePlaying = () => {
    attempts.current = 0;
    cancelRetry();
    setError(null);
  };

  /**
   * The clock stopped moving without the element admitting anything is wrong.
   *
   * An element whose data runs out mid-part sits at the same currentTime
   * indefinitely and fires neither `error` nor `pause`, so nothing above would
   * ever hear about it. Only while the listener wants audio, and only when no
   * attempt is already pending.
   */
  useEffect(() => {
    if (!playing) return;
    let last = -1;
    const id = setInterval(() => {
      const el = audio.current;
      if (!el || !wanted.current || retryTimer.current || recovering.current) return;
      const now = el.currentTime;
      if (!el.paused && !el.ended && now === last) resume();
      last = now;
    }, STALL_CHECK_MS);
    return () => clearInterval(id);
  }, [playing, resume]);

  /**
   * Coming back — to the network, or to the screen — is the best news there is.
   *
   * Both reset the count, because the reason every attempt so far failed has
   * just gone away. A phone asleep in a pocket exhausts its attempts in four
   * minutes and then waits here, which is why unlocking it starts the audio
   * again rather than showing a stopped player.
   */
  useEffect(() => {
    const again = () => {
      const el = audio.current;
      if (!el) return;
      attempts.current = 0;
      if (!wanted.current) return;
      setError(null);
      if (el.paused || el.ended) resume();
    };
    const onVisible = () => {
      if (!document.hidden) again();
    };
    window.addEventListener("online", again);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", again);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [resume]);

  // Nothing of this player's may outlive it: a retry that fires after the
  // reader has left calls play() on an element that is no longer in the page.
  useEffect(
    () => () => {
      wanted.current = false;
      cancelRetry();
    },
    [cancelRetry],
  );

  /**
   * The lock screen, and why it belongs to this fix rather than beside it.
   *
   * Without a media session, Android has no title for what is playing, no
   * buttons for it — and, the part that matters here, no way to tell this
   * player that a pause came from the listener. handlePause treats an
   * unexplained pause as an interruption and picks playback back up, which
   * without these handlers would fight anyone pressing pause on their lock
   * screen. They are what makes recovering from a pause safe.
   */
  useEffect(() => {
    const session = navigator.mediaSession;
    if (!session) return;
    if (title && typeof MediaMetadata !== "undefined") {
      session.metadata = new MediaMetadata({ title });
    }
    const handlers: Partial<Record<MediaSessionAction, MediaSessionActionHandler>> = {
      play: () => start(),
      pause: () => stop(),
      seekbackward: (d) => seek(Math.max(0, positionRef.current - (d.seekOffset ?? 10) * 1000)),
      seekforward: (d) => seek(positionRef.current + (d.seekOffset ?? 10) * 1000),
      seekto: (d) => {
        if (d.seekTime != null) seek(d.seekTime * 1000);
      },
    };
    for (const action of SESSION_ACTIONS) {
      // Not every browser knows every action, and setting an unknown one
      // throws rather than being ignored.
      try {
        session.setActionHandler(action, handlers[action] ?? null);
      } catch {
        /* this browser does not offer that button */
      }
    }
    return () => {
      for (const action of SESSION_ACTIONS) {
        try {
          session.setActionHandler(action, null);
        } catch {
          /* nothing to take down */
        }
      }
    };
  }, [title, seek, start, stop]);

  useEffect(() => {
    const session = navigator.mediaSession;
    if (session) session.playbackState = playing ? "playing" : "paused";
  }, [playing]);

  /**
   * The element's events, attached by hand rather than as JSX props, because
   * the element may be the page's (sharedAudio.ts). Through a ref, so each
   * fires this render's handler without re-attaching four times a second.
   */
  const handlers = useRef<Record<string, () => void>>({});
  handlers.current = {
    timeupdate: handleTimeUpdate,
    seeked: () => {
      seeking.current = false;
    },
    ended: handleEnded,
    pause: handlePause,
    playing: handlePlaying,
    error: resume,
  };
  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    const listeners = Object.keys(handlers.current).map(
      (type) => [type, () => handlers.current[type]?.()] as const,
    );
    for (const [type, fn] of listeners) el.addEventListener(type, fn);
    return () => {
      for (const [type, fn] of listeners) el.removeEventListener(type, fn);
      if (!shared) return;
      // The page's element outlives this chapter: leave it silent and empty,
      // so the next chapter's player starts from nothing of this one's.
      wanted.current = false;
      el.pause();
      delete el.dataset.partIndex;
      el.removeAttribute("src");
      el.load();
    };
  }, [shared]);

  // A reader's pictures open full screen and can be reported (0122).
  const pictures = useMemo(() => (bookID ? { bookID, chapterID } : null), [bookID, chapterID]);
  return (
    <PicturePlace.Provider value={pictures}>
      <section data-testid="player">
        {!shared && <audio ref={audio} preload="metadata" data-testid="audio" />}

        {/* The transcript comes first in the document and the transport is pinned
          under it. Text at the top, controls under the thumb: this is read
          one-handed on a phone, and a bar at the top of a long scrolling page
          is the one place a thumb cannot reach. */}
        {error && (
          <p
            role="alert"
            data-testid="player-error"
            className="mb-4 text-sm text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        )}

        {/* A render with failed parts has holes in it: the text is all on screen
          and the voice does not say some of it (ADR-0063). Said once, at the
          top, because the manifest carries the render's status and not yet
          where the holes are — locating them is its own slice. Not an alert:
          most of the chapter plays, and this is a caveat, not a failure. */}
        {manifest.status === "partial" && (
          <p data-testid="audio-incomplete" className="mb-4 text-sm text-muted">
            {t.player.audioIncomplete}
          </p>
        )}

        {/* Selection is suppressed across the whole transcript: a long press
          otherwise starts a native text selection that fights the sheet.

          dir="auto" lives here and *only* here. An element with dir="auto"
          skips any descendant that carries its own dir attribute when it looks
          for the first strong directional character — so putting dir="auto" on
          the sentences as well leaves this element with nothing to read and it
          falls back to ltr, right-aligning nothing and laying a Persian chapter
          out left to right. Nested dir="auto" is worse than none.
          pre-line: the text between words carries the author's line breaks. */}
        <article
          dir="auto"
          data-testid="transcript"
          className={`reading-text whitespace-pre-line ${allowCorrections || allowAsking ? "select-none" : ""}`}
          /* Selection is suppressed only where a long press means something. A
           reader who cannot correct anything should still be able to select a
           passage and copy it. */
          style={
            allowCorrections || allowAsking
              ? { WebkitUserSelect: "none", WebkitTouchCallout: "none" }
              : undefined
          }
        >
          {figures.get(FIGURE_TOP)?.map((image) => (
            <ChapterFigure key={image.id} image={image} />
          ))}
          {sentences.map((sentence, i) => (
            <SentenceViewWithFigures
              key={`${sentence.offset}-${i}`}
              figures={figures.get(i)}
              sentence={sentence}
              active={i === activeSentence}
              positionMs={positionMs}
              activeRef={
                i === activeSentence ? (scroll.activeRef as React.Ref<HTMLSpanElement>) : undefined
              }
              onSeek={seek}
              onCorrect={
                allowCorrections
                  ? (word) => setChanging({ word, sentence: sentence.text })
                  : allowAsking
                    ? (word) => openChat(sentence.text, true, cleanWord(word))
                    : undefined
              }
              onReopen={
                allowAsking && asked.has(sentence.text) ? () => openChat(sentence.text) : undefined
              }
              speakers={speakers}
              emphasis={manifest.emphasis}
            />
          ))}
        </article>

        {footer}

        {/* Spacer: the fixed bar would otherwise sit on top of the last lines of
          the chapter, and a reader would never see the end of it. */}
        <div aria-hidden="true" className={allowAsking ? "h-40" : "h-24"} />

        <div
          ref={scroll.barRef}
          className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-ground/95 backdrop-blur-md"
        >
          {/* Ask floats just above the transport at the end edge, as in the
            app: under the thumb, and clear of where lines start. It opens
            about the line being read. */}
          {allowAsking && (
            <AskButton
              onClick={() => openChat(sentences[activeSentence]?.text ?? null, false)}
              className="absolute end-4 bottom-full mb-3"
            />
          )}
          {/* Left to right in a Persian interface too. Time runs that way on the
            scrubber — its fill is painted "to right" — and a transport that
            flipped with the language would move Play under the other thumb
            for anyone who switches. */}
          <div
            dir="ltr"
            className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
          >
            {/* A round icon button rather than a labelled pill: this is the one
              control a listener hits repeatedly, often without looking, so it
              wants a large target and a shape the thumb can find. The
              accessible name still says Play or Pause. */}
            <SpeedButton speed={speed} onChange={setSpeed} />

            <button
              type="button"
              data-testid="play-pause"
              aria-label={playing ? t.player.pause : t.player.play}
              onClick={toggle}
              className="grid size-12 shrink-0 place-items-center rounded-full bg-sage text-on-sage transition-colors hover:bg-sage-deep"
            >
              <svg viewBox="0 0 24 24" className="size-5" fill="currentColor" aria-hidden="true">
                {playing ? (
                  <path d="M8 5.5h3v13H8zM13 5.5h3v13h-3z" />
                ) : (
                  <path d="M8.5 5.6a.6.6 0 0 1 .93-.5l8.2 5.4a.72.72 0 0 1 0 1.2l-8.2 5.4a.6.6 0 0 1-.93-.5z" />
                )}
              </svg>
            </button>

            <div className="min-w-0 flex-1">
              {/* A real range input, so dragging to scrub works and the keyboard
                gets arrow keys for free. The visible track is the input itself,
                styled — a div painted underneath would not be draggable. */}
              <input
                type="range"
                min={0}
                max={Math.max(1, manifest.duration_ms)}
                value={Math.min(positionMs, manifest.duration_ms)}
                step={1000}
                data-testid="scrubber"
                aria-label={t.player.position}
                aria-valuetext={t.player.positionOf(clock(positionMs), clock(manifest.duration_ms))}
                onChange={(e) => seek(Number(e.target.value))}
                className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-sunk accent-[var(--c-sage)]"
                style={{
                  background:
                    `linear-gradient(to right, var(--c-sage) ${fractionOf(manifest, positionMs) * 100}%, ` +
                    `var(--c-sunk) ${fractionOf(manifest, positionMs) * 100}%)`,
                }}
              />
              <div className="mt-1 flex items-center justify-between text-xs text-muted">
                <span className="tabular" data-testid="position">
                  {clock(positionMs)}
                </span>
                <label className="flex cursor-pointer items-center gap-1.5 select-none">
                  <input
                    type="checkbox"
                    data-testid="autoscroll"
                    checked={scroll.enabled}
                    onChange={(e) => scroll.setEnabled(e.target.checked)}
                    className="size-3.5 accent-[var(--c-sage)]"
                  />
                  {t.player.follow}
                </label>
                <span className="tabular" data-testid="duration">
                  {clock(manifest.duration_ms)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {chatOpen && (
          <ChatSheet
            chapterID={chapterID}
            chapterNumber={chapterNumber}
            bookID={bookID}
            threads={threads}
            line={chatContext ?? ""}
            offerCast={chatChosen}
            word={chatWord}
            onVisit={visitCitation}
            onClose={() => setChatOpen(false)}
          />
        )}

        {changing && (
          <ChangeSheet
            word={changing.word}
            sentence={changing.sentence}
            onFix={() => {
              setCorrecting(changing);
              setChanging(null);
            }}
            onEdit={
              onEditLine
                ? () => {
                    setChanging(null);
                    onEditLine(changing.sentence, false);
                  }
                : undefined
            }
            onRewrite={
              onEditLine && allowRewrite
                ? () => {
                    setChanging(null);
                    onEditLine(changing.sentence, true);
                  }
                : undefined
            }
            onClose={() => setChanging(null)}
          />
        )}

        {correcting && (
          <CorrectionSheet
            chapterID={chapterID}
            word={correcting.word}
            sentence={correcting.sentence}
            onClose={() => setCorrecting(null)}
          />
        )}
      </section>
    </PicturePlace.Provider>
  );
}

/**
 * SentenceViewWithFigures is one sentence and any pictures that follow it.
 *
 * A wrapper rather than a figure rendered inside SentenceView: a sentence is
 * an inline run of words, and a <figure> inside it would be a block box in the
 * middle of a line — which is where the picture would land on the page.
 */
function SentenceViewWithFigures({
  figures,
  ...props
}: React.ComponentProps<typeof SentenceView> & { figures?: ChapterImage[] }) {
  if (!figures?.length) return <SentenceView {...props} />;
  return (
    <>
      <SentenceView {...props} />
      {figures.map((image) => (
        <ChapterFigure key={image.id} image={image} />
      ))}
    </>
  );
}

/**
 * SentenceView draws one sentence: its words, and all the text between them.
 *
 * Listen shows the chapter as written (ADR-0163): what no voice says — a
 * play's speaker name, a bracketed stage direction, a section number — sits
 * in a word's `before` or `after`, and every character of both is drawn. Only
 * the highlight skips it. Pinned by testdata/player/listen-text.json, which
 * the app's layout reads too (mobile/lib/player/transcript.dart).
 */
export function SentenceView({
  sentence,
  active,
  positionMs,
  activeRef,
  onSeek,
  onCorrect,
  onReopen,
  speakers,
  emphasis,
}: {
  sentence: Sentence;
  active: boolean;
  positionMs: number;
  activeRef?: React.Ref<HTMLSpanElement>;
  onSeek: (ms: number) => void;
  onCorrect?: (word: string) => void;
  /** Set when the reader's chat is about this line: marks it (AskedMark). */
  onReopen?: () => void;
  speakers?: Speakers | null;
  /** The italics of the manifest's revision, as byte spans (ADR-0205). */
  emphasis?: Spans;
}) {
  const activeWord = active ? wordAt(sentence, positionMs) : -1;
  // The sentence's closing punctuation stays inside its highlight; the space
  // or line break after it goes outside, so a highlight never trails onto an
  // empty line.
  const last = sentence.words.length - 1;
  const [closing, following] = splitTail(sentence.words[last]?.after ?? " ");

  return (
    <>
      <span
        ref={activeRef}
        data-testid={active ? "sentence-active" : "sentence"}
        data-line
        data-asked={onReopen ? "true" : undefined}
        /* The sentence being read is tinted sky, not amber. The original icon's
         veins glowed cyan, and amber is spoken for by warnings — a page where
         "being read aloud" and "something needs attention" are the same colour
         makes neither of them legible. */
        className={
          active
            ? "-mx-[0.1em] rounded-[0.3rem] bg-reading box-decoration-clone px-[0.1em] py-[0.18em] shadow-[inset_0_-1px_0_var(--c-reading-edge)]"
            : undefined
        }
      >
        {sentence.words.map((word, i) => {
          // Byte positions in the manifest's revision, where the server placed
          // each quote (ADR-0125): the text around a word, and the word itself.
          const afterStart = word.offset + word.length;
          const before = word.before
            ? withRules(word.before, word.offset - utf8Length(word.before), speakers, emphasis)
            : undefined;
          const mark = speakers?.byStart.get(word.offset);
          return (
            <WordView
              key={`${word.offset}-${i}`}
              text={
                emphasis?.length ? withSpeakers(word.text, word.offset, null, emphasis) : word.text
              }
              before={
                mark ? (
                  <>
                    {before}
                    <SpeakerMark line={mark} speakers={speakers!} />
                  </>
                ) : (
                  before
                )
              }
              after={withRules(
                i === last ? closing : (word.after ?? " "),
                afterStart,
                speakers,
                emphasis,
              )}
              startMs={word.start_ms}
              active={i === activeWord}
              asked={!!onReopen}
              onTap={() => onSeek(word.start_ms)}
              onLongPress={onCorrect ? () => onCorrect(word.text) : undefined}
            />
          );
        })}
        {onReopen && <AskedMark onOpen={onReopen} />}
      </span>
      {withRules(
        following,
        (sentence.words[last] ? sentence.words[last].offset + sentence.words[last].length : 0) +
          utf8Length(closing),
        speakers,
        emphasis,
      )}
    </>
  );
}

/**
 * withRules draws the text between two words, with any whole line of it that
 * is only a section number — the Tao Te Ching's "1" in ".\n\n1\n\n" — as a
 * divider (SectionRule), as Read draws it. The divider is a box with its own
 * margins, so the break just before it and the blank lines after it go: in a
 * pre-line block they would add an empty line below it and none above.
 * `start` is where the text starts in the chapter, in bytes; a line opens at a
 * line break, or at the start of the chapter.
 */
export function withRules(
  text: string,
  start: number,
  speakers: Speakers | null | undefined,
  emphasis?: Spans,
): ReactNode {
  const pieces: ReactNode[] = [];
  let from = 0;
  for (let at = 0; at < text.length;) {
    const end = text.indexOf("\n", at);
    if (end < 0) break;
    const opens = at > 0 ? text[at - 1] === "\n" : start === 0;
    const number = opens ? sectionNumberOf(text.slice(at, end)) : null;
    if (number === null || text.slice(at, end).trim() === "") {
      at = end + 1;
      continue;
    }
    const cut = at > 0 ? at - 1 : 0;
    if (cut > from) {
      pieces.push(
        <Fragment key={from}>
          {withGaps(
            text.slice(from, cut),
            start + utf8Length(text.slice(0, from)),
            speakers,
            emphasis,
          )}
        </Fragment>,
      );
    }
    pieces.push(<SectionRule key={`rule-${start + at}`} number={number} />);
    // The blank lines after it, up to the last break before the next text.
    const rest = text.slice(end);
    from = end + (rest.length - rest.replace(/^\s*\n/, "").length);
    at = from;
  }
  if (from === 0) return withGaps(text, start, speakers, emphasis);
  if (from < text.length) {
    pieces.push(
      <Fragment key={from}>
        {withGaps(text.slice(from), start + utf8Length(text.slice(0, from)), speakers, emphasis)}
      </Fragment>,
    );
  }
  return <>{pieces}</>;
}

/** splitTail cuts text at its first whitespace: [before it, from it on]. */
export function splitTail(text: string): [string, string] {
  const at = text.search(/\s/);
  return at < 0 ? [text, ""] : [text.slice(0, at), text.slice(at)];
}

function WordView({
  text,
  before,
  after,
  startMs,
  active,
  asked,
  onTap,
  onLongPress,
}: {
  /** The word, as text or drawn with its emphasis. */
  text: ReactNode;
  /** The text around the word that is not a word (api.ts Word), with any
   *  speaker's avatar placed in it. */
  before?: ReactNode;
  after: ReactNode;
  startMs: number;
  active: boolean;
  /** In a line the reader's chat is about. */
  asked: boolean;
  onTap: () => void;
  onLongPress?: () => void;
}) {
  // usePressGesture already treats a missing onLongPress as "tap only", so a
  // reader gets no press timer at all rather than one that fires into nothing.
  const press = usePressGesture({ onTap, onLongPress });
  return (
    <>
      {before}
      {/* A real <button>, not a span with role="button": Enter and Space then
          fire a click natively, so the keyboard path and the pointer path are
          the same handler rather than two that can drift apart. */}
      <button
        type="button"
        {...press}
        // The testid changes when a word becomes active, so an index-based
        // locator shifts as playback moves. The start time does not.
        data-start-ms={startMs}
        data-testid={active ? "word-active" : "word"}
        className={
          // The padding is the highlight's, not the text's: a negative margin
          // gives it back, so a word sits exactly where the text puts it. As
          // bare padding it widened every space and opened a gap before each
          // comma and full stop ("Castle .", owner 10-23). The same holds
          // upright: a button is an atomic box, so its padding made every line
          // 0.36em taller than the line height says.
          "-mx-[0.1em] -my-[0.18em] inline cursor-pointer rounded-[0.25rem] px-[0.1em] py-[0.18em] text-inherit transition-colors " +
          // Per word, not on the sentence: a button is laid out as an atomic
          // inline box, so an underline set on its parent never reaches it.
          (asked ? `${askedWords} ` : "") +
          (active
            ? // A step up from the sentence tint it sits inside, not a second
              // unrelated colour: the eye is tracking one word moving through
              // one highlighted line.
              "bg-sage font-medium text-on-sage"
            : "hover:bg-sunk")
        }
      >
        {text}
      </button>
      {after}
    </>
  );
}
